import {PageStandard} from "../utils/PageStandard";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Drawer,
    FormControl,
    FormLabel,
    IconButton,
    Input,
    Option,
    Select,
    Sheet,
    Stack,
    Typography,
} from "@mui/joy";
import {Fragment, useEffect, useMemo, useState} from "react";
import {CategoriaModel, ServizioModel} from "../utils/Model";
import {
    Add,
    ArrowDownward,
    ArrowUpward,
    Delete,
    DeleteForever,
    Edit,
    FileDownload,
    Search,
    SwapVert,
} from "@mui/icons-material";
import {addService, deleteService, editServizio, getCategories, getServices} from "../utils/ViewModel";
import {MapContainer, Marker, TileLayer, useMapEvents} from "react-leaflet";
import {LatLng} from "leaflet";
import {downloadCSV, hasChanges} from "../utils/Utils";
import moment from "moment/moment";

type SortKey = "title" | "categoria"
type SortDir = "asc" | "desc"

const GRID_TEMPLATE_COLUMNS = "minmax(200px, 1.5fr) minmax(170px, 1fr) minmax(220px, 1.6fr) minmax(140px, 1fr) minmax(150px, 1fr) minmax(96px, 0.6fr)"

const HEADER_CELL_SX = {
    px: 1.5,
    py: 1,
    fontWeight: "lg",
    fontSize: "sm",
    bgcolor: "background.level2",
    borderBottom: "1px solid",
    borderColor: "divider",
    display: "flex",
    alignItems: "center",
    gap: 0.5,
} as const

const CELL_SX = {
    px: 1.5,
    py: 1,
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid",
    borderColor: "divider",
} as const

export function Servizi() {
    const [modalAddServizio, setModalAddServizio] = useState(false);

    const initialDataNewServizio: Omit<ServizioModel, "id"> = {
        categoryId: "",
        title: "",
        coordinates: {
            lat: 0,
            lng: 0,
        },
        address: "",
        phoneNumbers: [],
        openingHours: [],
        notes: "",
        booking: "",
        website: [],
        emailAddress: [],
    };
    const [newServizio, setNewServizio] = useState(initialDataNewServizio);

    const [servizi, setServizi] = useState<ServizioModel[]>([]);
    const [categorie, setCategorie] = useState<CategoriaModel[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [sortKey, setSortKey] = useState<SortKey>("title");
    const [sortDir, setSortDir] = useState<SortDir>("asc");

    useEffect(() => {
        loadData();
    }, []);

    function loadData() {
        getCategories().then((data) => {
            setCategorie(data);
        });
        getServices().then((data) => {
            setServizi(data);
            setLoading(false);
        });
    }

    function toggleSort(key: SortKey) {
        if (key === sortKey) {
            setSortDir(dir => dir === "asc" ? "desc" : "asc")
        } else {
            setSortKey(key)
            setSortDir("asc")
        }
    }

    // Aggiorna proprietà di newServizio in modo generico
    function handleChange<T extends keyof Omit<ServizioModel, "id">>(
        key: T,
        value: Omit<ServizioModel, "id">[T]
    ) {
        setNewServizio((old) => ({...old, [key]: value}));
    }

    // Gestione array generica (aggiungi, modifica, elimina)
    function handleArrayChange(
        key: keyof Omit<ServizioModel, "id">,
        index: number,
        value: string
    ) {
        const arr = newServizio[key] as string[] | undefined;
        if (!arr) return;
        const newArr = [...arr];
        newArr[index] = value;
        handleChange(key, newArr as any);
    }

    function addArrayItem(key: keyof Omit<ServizioModel, "id">) {
        const arr = newServizio[key] as string[] | undefined;
        const newArr = arr ? [...arr, ""] : [""];
        handleChange(key, newArr as any);
    }

    function removeArrayItem(key: keyof Omit<ServizioModel, "id">, index: number) {
        const arr = newServizio[key] as string[] | undefined;
        if (!arr) return;
        const newArr = arr.filter((_, i) => i !== index);
        handleChange(key, newArr as any);
    }

    const handleSubmit = () => {
        // Validazioni base coordinate rimangono da fare
        addService(newServizio)
            .then(() => {
                setModalAddServizio(false);
                setNewServizio(initialDataNewServizio);
                loadData();
            })
            .catch(() => {
                // gestione errore già fatto in ViewModel con alert
            });
    };

    function LocationMarker() {
        const map = useMapEvents({
            click(e) {
                setNewServizio({...newServizio, coordinates: {lat: e.latlng.lat, lng: e.latlng.lng}})
                map.flyTo(e.latlng, map.getZoom())
            },
        })

        return (newServizio.coordinates.lat === null || newServizio.coordinates.lng === null) ? null : (
            <Marker position={new LatLng(newServizio.coordinates.lat, newServizio.coordinates.lng)}/>
        )
    }

    const searchLower = search.trim().toLowerCase()

    const categoryById = useMemo(() => {
        const map = new Map<string, CategoriaModel>()
        categorie.forEach(c => map.set(c.id, c))
        return map
    }, [categorie])

    const filteredServizi = useMemo(() => {
        const filtered = servizi.filter(s => {
            if (categoryFilter !== "" && s.categoryId !== categoryFilter) return false
            if (searchLower === "") return true
            const categoriaTitle = categoryById.get(s.categoryId)?.title ?? ""
            const haystack = `${s.title ?? ""} ${s.address ?? ""} ${categoriaTitle}`.toLowerCase()
            return haystack.includes(searchLower)
        })

        const sorted = [...filtered].sort((a, b) => {
            let cmp: number
            if (sortKey === "title") {
                cmp = (a.title ?? "").localeCompare(b.title ?? "")
            } else {
                const catA = categoryById.get(a.categoryId)?.title ?? ""
                const catB = categoryById.get(b.categoryId)?.title ?? ""
                cmp = catA.localeCompare(catB)
            }
            return sortDir === "asc" ? cmp : -cmp
        })

        return sorted
    }, [servizi, searchLower, categoryFilter, sortKey, sortDir, categoryById])

    return (
        <PageStandard
            title="Servizi"
            sidebarVisible={true}
            headerEnd={
                <Stack direction="row" spacing={2}>
                    <Button onClick={() => setModalAddServizio(true)}>Aggiungi servizio</Button>
                    <Button startDecorator={<FileDownload/>}
                            onClick={() => {
                                downloadCSV<ServizioModel>({
                                    data: servizi,
                                    fields: ["id", "categoryId", "title", "coordinates.lat", "coordinates.lng", "address", "phoneNumbers", "openingHours", "notes", "booking", "website", "emailAddress"],
                                    filename: `oasi-servizi-${moment().format("YYYY-MM-DD")}.csv`,
                                })
                            }}
                    >
                        Esporta tutti in CSV
                    </Button>
                </Stack>
            }
        >
            <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 2}} flexWrap="wrap">
                <Input
                    placeholder="Cerca per titolo, indirizzo o categoria..."
                    startDecorator={<Search/>}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    sx={{minWidth: 280, flexGrow: 1, maxWidth: 420}}
                />
                <Select
                    value={categoryFilter}
                    onChange={(_, val) => setCategoryFilter(val ?? "")}
                    sx={{minWidth: 220}}
                >
                    <Option value="">Tutte le categorie</Option>
                    {categorie.map(c => (
                        <Option value={c.id} key={c.id}>{c.icon} {c.title}</Option>
                    ))}
                </Select>
                <Chip size="sm" variant="soft" color="neutral">{filteredServizi.length} servizi</Chip>
            </Stack>

            {
                loading &&
                <Stack alignItems="center" justifyContent="center" sx={{py: 6}}>
                    <CircularProgress/>
                </Stack>
            }

            {
                !loading && filteredServizi.length === 0 &&
                <Typography level="body-md" sx={{textAlign: "center", py: 4}}>
                    Nessun servizio trovato{searchLower !== "" ? ` per "${search}"` : ""}.
                </Typography>
            }

            {
                !loading && filteredServizi.length > 0 &&
                <Box sx={{overflowX: "auto", pb: 5}}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
                            minWidth: 1100,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "md",
                            overflow: "hidden",
                        }}
                    >
                        <SortableHeaderCell label="Titolo" active={sortKey === "title"} dir={sortDir}
                                             onClick={() => toggleSort("title")}/>
                        <SortableHeaderCell label="Categoria" active={sortKey === "categoria"} dir={sortDir}
                                             onClick={() => toggleSort("categoria")}/>
                        <Box sx={HEADER_CELL_SX}>Indirizzo</Box>
                        <Box sx={HEADER_CELL_SX}>Telefono</Box>
                        <Box sx={HEADER_CELL_SX}>ID</Box>
                        <Box sx={{...HEADER_CELL_SX, justifyContent: "center"}}>Azioni</Box>

                        {filteredServizi.map((s, idx) => (
                            <RowServizio
                                key={s.id}
                                data={s}
                                categorie={categorie}
                                loadData={loadData}
                                zebra={idx % 2 === 1}
                            />
                        ))}
                    </Box>
                </Box>
            }

            <Drawer open={modalAddServizio} onClose={() => setModalAddServizio(false)}
                    anchor="right"
                    size="md"
                    variant="plain"
                    slotProps={{
                        content: {
                            sx: {
                                bgcolor: 'transparent',
                                p: {md: 3, sm: 0},
                                boxShadow: 'none',
                                minWidth: "700px"
                            },
                        },
                    }}
            >
                <Sheet
                    sx={{
                        borderRadius: 'xl',
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        height: '100%',
                        overflow: 'auto',
                    }}
                >
                    <Stack direction="column" spacing={2} p={3}>
                        <Typography component="h2" level="h4" sx={{fontWeight: "lg", mb: 1}}>
                            Aggiungi servizio
                        </Typography>
                        <FormControl>
                            <FormLabel>Categoria</FormLabel>
                            <Select
                                value={newServizio.categoryId}
                                onChange={(e, newVal) => handleChange("categoryId", newVal!)}
                            >
                                <Option value="" disabled>
                                    Scegli
                                </Option>
                                {categorie.map((c) => (
                                    <Option value={c.id} key={c.id}>
                                        {c.icon} {c.title}
                                    </Option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Titolo servizio</FormLabel>
                            <Input
                                type="text"
                                value={newServizio.title}
                                onChange={(e) => handleChange("title", e.target.value)}
                                placeholder="Titolo servizio"
                            />
                        </FormControl>

                        {/* Location */}
                        <FormControl>
                            <FormLabel>Posizione</FormLabel>
                            <div style={{height: '400px'}}>
                                <MapContainer center={[45.07049, 7.68682]} zoom={16} scrollWheelZoom={true}
                                              className="map-container-leaflet">
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    <LocationMarker/>
                                </MapContainer>
                            </div>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Indirizzo</FormLabel>
                            <Input
                                type="text"
                                value={newServizio.address ?? ""}
                                onChange={(e) => handleChange("address", e.target.value)}
                                placeholder="Via Roma 1"
                            />
                        </FormControl>

                        {/* Phone Numbers */}
                        <FormControl>
                            <FormLabel>
                                Numeri di telefono
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("phoneNumbers")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(newServizio.phoneNumbers || []).map((num, index) => (
                                <Input
                                    key={index}
                                    type="text"
                                    placeholder="333 000 0000"
                                    value={num}
                                    onChange={(e) => handleArrayChange("phoneNumbers", index, e.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("phoneNumbers", index)}
                                            disabled={(newServizio.phoneNumbers?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        {/* Opening Hours */}
                        <FormControl>
                            <FormLabel>
                                Orari di apertura
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("openingHours")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(newServizio.openingHours || []).map((o, index) => (
                                <Input
                                    key={index}
                                    type="text"
                                    placeholder="Lun-Ven 9:00 - 18:00"
                                    value={o}
                                    onChange={(e) => handleArrayChange("openingHours", index, e.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("openingHours", index)}
                                            disabled={(newServizio.openingHours?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        {/* Notes */}
                        <FormControl>
                            <FormLabel>Note</FormLabel>
                            <Input
                                type="text"
                                value={newServizio.notes ?? ""}
                                onChange={(e) => handleChange("notes", e.target.value)}
                                placeholder="Note aggiuntive"
                            />
                        </FormControl>

                        {/* Booking */}
                        <FormControl>
                            <FormLabel>Prenotazione</FormLabel>
                            <Input
                                type="text"
                                value={newServizio.booking ?? ""}
                                onChange={(e) => handleChange("booking", e.target.value)}
                                placeholder="Modalità di prenotazione"
                            />
                        </FormControl>

                        {/* Website */}
                        <FormControl>
                            <FormLabel>
                                Siti web
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("website")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(newServizio.website || []).map((w, index) => (
                                <Input
                                    key={index}
                                    type="text"
                                    placeholder="https://example.com"
                                    value={w}
                                    onChange={(e) => handleArrayChange("website", index, e.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("website", index)}
                                            disabled={(newServizio.website?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        {/* Email Address */}
                        <FormControl>
                            <FormLabel>
                                Email
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("emailAddress")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(newServizio.emailAddress || []).map((e, index) => (
                                <Input
                                    key={index}
                                    type="email"
                                    placeholder="esempio@email.it"
                                    value={e}
                                    onChange={(evt) => handleArrayChange("emailAddress", index, evt.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("emailAddress", index)}
                                            disabled={(newServizio.emailAddress?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        <Button
                            fullWidth
                            disabled={
                                newServizio.categoryId.trim().length === 0 ||
                                newServizio.title.trim().length === 0 ||
                                newServizio.coordinates.lat === 0 ||
                                newServizio.coordinates.lng === 0
                            }
                            onClick={handleSubmit}
                        >
                            Aggiungi
                        </Button>
                    </Stack>
                </Sheet>
            </Drawer>
        </PageStandard>
    );
}

function SortableHeaderCell(props: { label: string, active: boolean, dir: SortDir, onClick: () => void }) {
    return (
        <Box
            sx={{...HEADER_CELL_SX, cursor: "pointer", userSelect: "none"}}
            onClick={props.onClick}
        >
            {props.label}
            {
                props.active ?
                    (props.dir === "asc" ? <ArrowUpward sx={{fontSize: 16}}/> : <ArrowDownward sx={{fontSize: 16}}/>)
                    :
                    <SwapVert sx={{fontSize: 16, opacity: 0.35}}/>
            }
        </Box>
    )
}

function RowServizio(props: {
    data: ServizioModel;
    categorie: CategoriaModel[];
    loadData: () => void;
    zebra: boolean;
}) {
    const categorie = props.categorie;
    const loadData = props.loadData;
    const defaultdata = props.data

    const [data, setData] = useState<ServizioModel>(props.data);
    const [modalEdit, setModalEdit] = useState(false)

    const categoria = categorie.find((c) => c.id === defaultdata.categoryId);
    const zebraSx = props.zebra ? {bgcolor: "background.level1"} : undefined

    // Aggiorna proprietà di newServizio in modo generico
    function handleChange<T extends keyof ServizioModel>(
        key: T,
        value: ServizioModel[T]
    ) {
        setData((old) => ({...old, [key]: value}));
    }

    // Gestione array generica (aggiungi, modifica, elimina)
    function handleArrayChange(
        key: keyof ServizioModel,
        index: number,
        value: string
    ) {
        const arr = data[key] as string[] | undefined;
        if (!arr) return;
        const newArr = [...arr];
        newArr[index] = value;
        handleChange(key, newArr as any);
    }

    function addArrayItem(key: keyof ServizioModel) {
        const arr = data[key] as string[] | undefined;
        const newArr = arr ? [...arr, ""] : [""];
        handleChange(key, newArr as any);
    }

    function removeArrayItem(key: keyof ServizioModel, index: number) {
        const arr = data[key] as string[] | undefined;
        if (!arr) return;
        const newArr = arr.filter((_, i) => i !== index);
        handleChange(key, newArr as any);
    }

    const handleSubmit = () => {
        editServizio(data)
            .then(() => {
                setModalEdit(false);
                loadData();
            })
    };

    function LocationMarker() {
        const map = useMapEvents({
            click(e) {
                setData({...data, coordinates: {lat: e.latlng.lat, lng: e.latlng.lng}})
                map.flyTo(e.latlng, map.getZoom())
            },
        })

        return (data.coordinates.lat === null || data.coordinates.lng === null) ? null : (
            <Marker position={new LatLng(data.coordinates.lat, data.coordinates.lng)}/>
        )
    }

    return (
        <Fragment>
            <Box sx={{...CELL_SX, ...zebraSx}}>
                <Typography level="body-sm" fontWeight="lg">{defaultdata.title}</Typography>
            </Box>
            <Box sx={{...CELL_SX, ...zebraSx}}>
                {categoria ? (
                    <Chip size="sm" color="neutral" variant="outlined">
                        {categoria.icon} {categoria.title}
                    </Chip>
                ) : (
                    <Chip size="sm" color="warning" variant="soft">
                        Nessuna categoria
                    </Chip>
                )}
            </Box>
            <Box sx={{...CELL_SX, ...zebraSx}}>
                <Typography level="body-sm" noWrap>{defaultdata.address || "—"}</Typography>
            </Box>
            <Box sx={{...CELL_SX, ...zebraSx}}>
                <Typography level="body-sm" noWrap>
                    {(defaultdata.phoneNumbers && defaultdata.phoneNumbers.length > 0) ? defaultdata.phoneNumbers.join(", ") : "—"}
                </Typography>
            </Box>
            <Box sx={{...CELL_SX, ...zebraSx}}>
                <Typography level="body-xs" sx={{fontFamily: "monospace"}} noWrap>{defaultdata.id}</Typography>
            </Box>
            <Box sx={{...CELL_SX, ...zebraSx, justifyContent: "center", gap: 1}}>
                <IconButton
                    color="primary"
                    variant="outlined"
                    size="sm"
                    onClick={() => setModalEdit(true)}
                >
                    <Edit fontSize="small"/>
                </IconButton>
                <IconButton
                    color="danger"
                    variant="outlined"
                    size="sm"
                    onClick={() => {
                        if (
                            window.confirm(`Eliminare definitivamente il servizio ${defaultdata.title} ?`)
                        ) {
                            deleteService(defaultdata.id).then(() => {
                                loadData();
                            });
                        }
                    }}
                >
                    <DeleteForever fontSize="small"/>
                </IconButton>
            </Box>

            <Drawer open={modalEdit} onClose={() => cancelModal()}
                    anchor="right"
                    size="md"
                    variant="plain"
                    slotProps={{
                        content: {
                            sx: {
                                bgcolor: 'transparent',
                                p: {md: 3, sm: 0},
                                boxShadow: 'none',
                                minWidth: "700px"
                            },
                        },
                    }}
            >
                <Sheet
                    sx={{
                        borderRadius: 'xl',
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        height: '100%',
                        overflow: 'auto',
                    }}
                >
                    <Stack direction="column" spacing={2} p={3}>
                        <Typography component="h2" level="h4" sx={{fontWeight: "lg", mb: 1}}>
                            Modifica servizio
                        </Typography>
                        <FormControl>
                            <FormLabel>Categoria</FormLabel>
                            <Select
                                value={data.categoryId}
                                onChange={(e, newVal) => handleChange("categoryId", newVal!)}
                            >
                                <Option value="" disabled>
                                    Scegli
                                </Option>
                                {categorie.map((c) => (
                                    <Option value={c.id} key={c.id}>
                                        {c.icon} {c.title}
                                    </Option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Titolo servizio</FormLabel>
                            <Input
                                type="text"
                                value={data.title ?? ""}
                                onChange={(e) => handleChange("title", e.target.value)}
                                placeholder="Titolo servizio"
                            />
                        </FormControl>

                        {/* Location */}
                        <FormControl>
                            <FormLabel>Posizione</FormLabel>
                            <div style={{height: '400px'}}>
                                <MapContainer center={[45.07049, 7.68682]} zoom={16} scrollWheelZoom={true}
                                              className="map-container-leaflet">
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    <LocationMarker/>
                                </MapContainer>
                            </div>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Indirizzo</FormLabel>
                            <Input
                                type="text"
                                value={data.address ?? ""}
                                onChange={(e) => handleChange("address", e.target.value)}
                                placeholder="Via Roma 1"
                            />
                        </FormControl>

                        {/* Phone Numbers */}
                        <FormControl>
                            <FormLabel>
                                Numeri di telefono
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("phoneNumbers")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(data.phoneNumbers || []).map((num, index) => (
                                <Input
                                    key={index}
                                    type="text"
                                    placeholder="333 000 0000"
                                    value={num}
                                    onChange={(e) => handleArrayChange("phoneNumbers", index, e.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("phoneNumbers", index)}
                                            disabled={(data.phoneNumbers?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        {/* Opening Hours */}
                        <FormControl>
                            <FormLabel>
                                Orari di apertura
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("openingHours")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(data.openingHours || []).map((o, index) => (
                                <Input
                                    key={index}
                                    type="text"
                                    placeholder="Lun-Ven 9:00 - 18:00"
                                    value={o}
                                    onChange={(e) => handleArrayChange("openingHours", index, e.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("openingHours", index)}
                                            disabled={(data.openingHours?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        {/* Notes */}
                        <FormControl>
                            <FormLabel>Note</FormLabel>
                            <Input
                                type="text"
                                value={data.notes ?? ""}
                                onChange={(e) => handleChange("notes", e.target.value)}
                                placeholder="Note aggiuntive"
                            />
                        </FormControl>

                        {/* Booking */}
                        <FormControl>
                            <FormLabel>Prenotazione</FormLabel>
                            <Input
                                type="text"
                                value={data.booking ?? ""}
                                onChange={(e) => handleChange("booking", e.target.value)}
                                placeholder="Modalità di prenotazione"
                            />
                        </FormControl>

                        {/* Website */}
                        <FormControl>
                            <FormLabel>
                                Siti web
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("website")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(data.website || []).map((w, index) => (
                                <Input
                                    key={index}
                                    type="text"
                                    placeholder="https://example.com"
                                    value={w}
                                    onChange={(e) => handleArrayChange("website", index, e.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("website", index)}
                                            disabled={(data.website?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        {/* Email Address */}
                        <FormControl>
                            <FormLabel>
                                Email
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("emailAddress")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(data.emailAddress || []).map((e, index) => (
                                <Input
                                    key={index}
                                    type="email"
                                    placeholder="esempio@email.it"
                                    value={e}
                                    onChange={(evt) => handleArrayChange("emailAddress", index, evt.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("emailAddress", index)}
                                            disabled={(data.emailAddress?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        <Stack direction="row" spacing={2}>
                            <Button fullWidth
                                    variant="outlined"
                                    color="neutral"
                                    onClick={() => cancelModal()}
                            >
                                Annulla
                            </Button>
                            <Button
                                fullWidth
                                disabled={
                                    (data.categoryId ?? "").trim().length === 0 ||
                                    (data.title ?? "").trim().length === 0 ||
                                    data.coordinates.lat === 0 ||
                                    data.coordinates.lng === 0 ||
                                    !hasChanges(defaultdata, data)
                                }
                                onClick={handleSubmit}
                            >
                                Modifica
                            </Button>
                        </Stack>

                    </Stack>
                </Sheet>
            </Drawer>
        </Fragment>
    )

    function cancelModal() {
        setModalEdit(false)
        setData(defaultdata)
    }
}
